import { msg } from '@lingui/core/macro';
import { loadingChunkRetry } from '@sit/client-shared';
import { createBrowserRouter, redirect } from 'react-router';
import { validate as isUuidV4 } from 'uuid';
import { config, intacctUrls } from './config/env';
import { EntriesApprovalsIndexLayout } from './containers/EntriesApprovals/Layout';
import NotFoundPage from './containers/NotFoundPage';
import { NotFoundMessage } from './containers/NotFoundPage/NotFoundMessage';
import type { MetaResolver } from './helpers/head-manager';
import { appendCurrentParamsToUrl } from './helpers/routing';
import { Root } from './root';
import { Component as DeepLinking } from './routes/deep-linking';
import * as EntriesApprovalsIndex from './routes/entries-approvals._index';
import { Component as ExternalAuth } from './routes/external-auth';
import * as Index from './routes/index';
import * as LeaveApprovalsIndex from './routes/leave-approvals._index';
import { Component as StaffTimesheets } from './routes/staff-timesheets';
import * as TimeApprovalsIndex from './routes/time-approvals._index';
import { Component as Timesheet } from './routes/timesheet.$id';
import { Component as Timesheets } from './routes/timesheets';

function redirectFromTo(from: string, to: string) {
  return function loader({ request }: { request: Request }) {
    const url = new URL(request.url);
    const prefix = from;
    const rest = url.pathname.startsWith(prefix) ? url.pathname.slice(prefix.length) : '';
    return redirect(`${to}${rest}${url.search}`);
  };
}

function redirectEntryModalChildToDetails() {
  return function loader({ request }: { request: Request }) {
    const url = new URL(request.url);
    const detailsPath = url.pathname.replace(/(\/entry\/[^/]+)\/.*$/, '$1/details');

    return redirect(`${detailsPath}${url.search}`);
  };
}

export const router = createBrowserRouter([
  {
    path: '*',
    Component: Root,
    handle: {
      meta: () => ({
        title: msg`Sage Intelligent Time`,
      }),
    } satisfies { meta: MetaResolver },
    children: [
      {
        index: true,
        loader: Index.loader,
      },
      {
        path: 'approvals/*',
        loader: redirectFromTo('/approvals', '/time-approvals'),
        handle: {
          meta: () => ({
            title: msg`Approvals | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
      },
      {
        path: 'data-sources',
        lazy: () => loadingChunkRetry(() => import('./routes/data-sources')),
        handle: {
          meta: () => ({
            title: msg`Data sources | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        children: [
          {
            index: true,
            lazy: () => loadingChunkRetry(() => import('./routes/data-sources._index')),
          },
          {
            path: 'calendar-missing-permission',
            lazy: () => loadingChunkRetry(() => import('./routes/data-sources.calendar-missing-permission')),
          },
          {
            path: 'time-assistant',
            lazy: () => loadingChunkRetry(() => import('./routes/data-sources.time-assistant')),
          },
          {
            path: ':sourceType',
            lazy: () => loadingChunkRetry(() => import('./routes/data-sources.$sourceType')),
            children: [
              {
                index: true,
                lazy: () => loadingChunkRetry(() => import('./routes/data-sources.$sourceType._index')),
              },
              {
                path: 'success',
                lazy: () => loadingChunkRetry(() => import('./routes/data-sources.$sourceType.success')),
              },
              {
                path: '*',
                Component: NotFoundMessage,
              },
            ],
          },
          {
            path: '*',
            Component: NotFoundMessage,
          },
        ],
      },
      {
        path: 'deep-linking',
        Component: DeepLinking,
        handle: {
          meta: () => ({
            title: msg`Loading...`,
          }),
        } satisfies { meta: MetaResolver },
      },
      {
        path: 'entries-approvals',
        Component: EntriesApprovalsIndexLayout,
        handle: {
          meta: () => ({
            title: msg`Entries approvals | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        children: [
          {
            index: true,
            loader: EntriesApprovalsIndex.loader,
          },
          {
            path: 'todo',
            lazy: () => loadingChunkRetry(() => import('./routes/entries-approvals.$approvalType')),
            children: [
              {
                path: ':id/entry/:entry_id',
                lazy: () => loadingChunkRetry(() => import('./routes/entries-approvals.$approvalType.$id.entry.$entry_id')),
                children: [
                  {
                    index: true,
                    lazy: () => loadingChunkRetry(() => import('./routes/entries-approvals.$approvalType.$id.entry.$entry_id._index')),
                  },
                  {
                    path: 'details',
                    lazy: () => loadingChunkRetry(() => import('./routes/entries-approvals.$approvalType.$id.entry.$entry_id.details')),
                  },
                  {
                    path: 'approval-history',
                    lazy: () =>
                      loadingChunkRetry(() => import('./routes/entries-approvals.$approvalType.$id.entry.$entry_id.approval-history')),
                  },
                  {
                    path: '*',
                    loader: redirectEntryModalChildToDetails(),
                  },
                ],
              },
            ],
          },
          {
            path: 'done',
            lazy: () => loadingChunkRetry(() => import('./routes/entries-approvals.$approvalType')),
            children: [
              {
                path: ':id/entry/:entry_id',
                lazy: () => loadingChunkRetry(() => import('./routes/entries-approvals.$approvalType.$id.entry.$entry_id')),
                children: [
                  {
                    index: true,
                    lazy: () => loadingChunkRetry(() => import('./routes/entries-approvals.$approvalType.$id.entry.$entry_id._index')),
                  },
                  {
                    path: 'details',
                    lazy: () => loadingChunkRetry(() => import('./routes/entries-approvals.$approvalType.$id.entry.$entry_id.details')),
                  },
                  {
                    path: 'approval-history',
                    lazy: () =>
                      loadingChunkRetry(() => import('./routes/entries-approvals.$approvalType.$id.entry.$entry_id.approval-history')),
                  },
                  {
                    path: '*',
                    loader: redirectEntryModalChildToDetails(),
                  },
                ],
              },
            ],
          },
          {
            path: '*',
            Component: NotFoundMessage,
          },
        ],
      },
      {
        path: 'external-auth',
        Component: ExternalAuth,
        handle: {
          meta: () => ({
            title: msg`Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
      },
      {
        path: 'home/*',
        loader: () => {
          window.location.href = intacctUrls.timeHomepage;
        },
      },
      {
        path: 'login',
        lazy: () => loadingChunkRetry(() => import('./routes/login')),
        handle: {
          meta: () => ({
            title: msg`Login | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
      },
      {
        path: 'privacy',
        lazy: () => loadingChunkRetry(() => import('./routes/privacy')),
        handle: {
          meta: () => ({
            title: msg`Privacy Policy | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
      },
      {
        path: 'rate-cards',
        lazy: () => loadingChunkRetry(() => import('./routes/rate-cards')),
        handle: {
          meta: () => ({
            title: msg`Rate cards | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        children: [
          {
            index: true,
            Component: () => null,
          },
          {
            path: 'new',
            lazy: () => loadingChunkRetry(() => import('./routes/rate-cards.new')),
          },
          {
            path: ':rateCard',
            lazy: () => loadingChunkRetry(() => import('./routes/rate-cards.$rateCard')),
          },
          {
            path: '*',
            Component: NotFoundMessage,
          },
        ],
      },
      {
        path: 'leave',
        lazy: () => loadingChunkRetry(() => import('./routes/leave')),
        handle: {
          meta: () => ({
            title: msg`Leave | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        children: [
          {
            index: true,
            lazy: () => loadingChunkRetry(() => import('./routes/leave._index')),
            handle: {
              meta: () => ({
                title: msg`My leave | Sage Intelligent Time`,
              }),
            } satisfies { meta: MetaResolver },
          },
          {
            path: 'team',
            lazy: () => loadingChunkRetry(() => import('./routes/leave.team')),
            handle: {
              meta: () => ({
                title: msg`Team leave | Sage Intelligent Time`,
              }),
            } satisfies { meta: MetaResolver },
          },
        ],
      },
      {
        path: 'leave-approvals',
        lazy: () => loadingChunkRetry(() => import('./routes/leave-approvals')),
        handle: {
          meta: () => ({
            title: msg`Leave approvals | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        children: [
          {
            index: true,
            loader: LeaveApprovalsIndex.loader,
          },
          {
            path: 'todo',
            lazy: () => loadingChunkRetry(() => import('./routes/leave-approvals.$approvalType')),
          },
          {
            path: 'done',
            lazy: () => loadingChunkRetry(() => import('./routes/leave-approvals.$approvalType')),
          },
        ],
      },
      {
        path: 'staff-leave',
        lazy: () => loadingChunkRetry(() => import('./routes/staff-leave')),
        handle: {
          meta: () => ({
            title: msg`Staff leave | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
      },
      {
        path: 'settings/*',
        loader: redirectFromTo('/settings', '/time-preferences'),
        handle: {
          meta: () => ({
            title: msg`Settings | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
      },
      {
        path: 'staff-timesheets',
        Component: StaffTimesheets,
        handle: {
          meta: () => ({
            title: msg`Staff timesheets | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
      },
      ...(config.VITE_APP_SHOW_CREW_TIMESHEETS
        ? [
            {
              path: 'crew-timesheets',
              lazy: () => loadingChunkRetry(() => import('./routes/crew-timesheets')),
              handle: {
                meta: () => ({
                  title: msg`Crew timesheets | Sage Intelligent Time`,
                }),
              } satisfies { meta: MetaResolver },
            },
            {
              path: 'crew-timesheets/:id',
              lazy: () => loadingChunkRetry(() => import('./routes/crew-timesheets.$id')),
              handle: {
                meta: () => ({
                  title: msg`Crew timesheet | Sage Intelligent Time`,
                }),
              } satisfies { meta: MetaResolver },
              children: [
                {
                  path: 'entry/:entry_id',
                  lazy: () => loadingChunkRetry(() => import('./routes/crew-timesheets.$id.entry.$entry_id')),
                  children: [
                    {
                      index: true,
                      lazy: () => loadingChunkRetry(() => import('./routes/timesheet.$id.entry.$entry_id._index')),
                    },
                    {
                      path: 'details',
                      lazy: () => loadingChunkRetry(() => import('./routes/timesheet.$id.entry.$entry_id.details')),
                    },
                    {
                      path: 'approval-history',
                      lazy: () => loadingChunkRetry(() => import('./routes/timesheet.$id.entry.$entry_id.approval-history')),
                    },
                    {
                      path: '*',
                      loader: redirectEntryModalChildToDetails(),
                    },
                  ],
                },
              ],
            },
          ]
        : []),
      {
        path: 'staff-time-clocks',
        lazy: () => loadingChunkRetry(() => import('./routes/staff-time-clocks')),
        handle: {
          meta: () => ({
            title: msg`Staff time clocks | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        children: [
          {
            index: true,
            Component: () => null,
          },
          {
            path: 'new',
            lazy: () => loadingChunkRetry(() => import('./routes/staff-time-clocks.new')),
          },
          {
            path: ':timeClockId',
            lazy: () => loadingChunkRetry(() => import('./routes/staff-time-clocks.$timeClockId')),
            children: [
              {
                path: 'breaks',
                lazy: () => loadingChunkRetry(() => import('./routes/staff-time-clocks.$timeClockId.breaks')),
              },
              {
                path: 'location',
                lazy: () => loadingChunkRetry(() => import('./routes/staff-time-clocks.$timeClockId.location')),
              },
            ],
          },
        ],
      },
      {
        path: 'timesheets',
        Component: Timesheets,
        handle: {
          meta: () => ({
            title: msg`Timesheets | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
      },
      {
        path: 'timesheet/:id',
        Component: Timesheet,
        handle: {
          meta: () => ({
            title: msg`Timesheet | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        children: [
          {
            path: 'entry/:entry_id',
            lazy: () => loadingChunkRetry(() => import('./routes/timesheet.$id.entry.$entry_id')),
            children: [
              {
                index: true,
                lazy: () => loadingChunkRetry(() => import('./routes/timesheet.$id.entry.$entry_id._index')),
              },
              {
                path: 'details',
                lazy: () => loadingChunkRetry(() => import('./routes/timesheet.$id.entry.$entry_id.details')),
              },
              {
                path: 'approval-history',
                lazy: () => loadingChunkRetry(() => import('./routes/timesheet.$id.entry.$entry_id.approval-history')),
              },
              {
                path: '*',
                loader: redirectEntryModalChildToDetails(),
              },
            ],
          },
        ],
      },
      {
        path: 'time-approvals',
        lazy: () => loadingChunkRetry(() => import('./routes/time-approvals')),
        handle: {
          meta: () => ({
            title: msg`Time approvals | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        children: [
          {
            index: true,
            loader: TimeApprovalsIndex.loader,
          },
          {
            path: 'todo',
            lazy: () => loadingChunkRetry(() => import('./routes/time-approvals.$approvalType')),
          },
          {
            path: 'done',
            lazy: () => loadingChunkRetry(() => import('./routes/time-approvals.$approvalType')),
          },
        ],
      },
      {
        path: 'time-approvals/:id',
        lazy: () => loadingChunkRetry(() => import('./routes/time-approvals.$id')),
        handle: {
          meta: () => ({
            title: msg`Time approval | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        loader: ({ request }) => {
          const url = new URL(request.url);
          // Get the :id, validate that it's a UUID
          const id = url.pathname.split('/')[2];
          if (!id || !isUuidV4(id)) {
            const redirectUrl = appendCurrentParamsToUrl(url, '/time-approvals/todo');
            return redirect(redirectUrl);
          }
        },
        children: [
          {
            index: true,
            Component: () => null,
          },
          {
            path: 'entry/:entry_id',
            lazy: () => loadingChunkRetry(() => import('./routes/time-approvals.$id.entry.$entry_id')),
            children: [
              {
                index: true,
                lazy: () => loadingChunkRetry(() => import('./routes/time-approvals.$id.entry.$entry_id._index')),
              },
              {
                path: 'details',
                lazy: () => loadingChunkRetry(() => import('./routes/time-approvals.$id.entry.$entry_id.details')),
              },
              {
                path: 'approval-history',
                lazy: () => loadingChunkRetry(() => import('./routes/time-approvals.$id.entry.$entry_id.approval-history')),
              },
              {
                path: '*',
                loader: ({ params, request }) => {
                  const url = new URL(request.url);
                  const redirectUrl = appendCurrentParamsToUrl(url, `/time-approvals/${params.id}/entry/${params.entry_id}/details`);
                  return redirect(redirectUrl);
                },
              },
            ],
          },
          {
            path: '*',
            Component: NotFoundMessage,
          },
        ],
      },
      {
        path: 'time-clocks',
        lazy: () => loadingChunkRetry(() => import('@web/routes/time-clocks')),
        handle: {
          meta: () => ({
            title: msg`Time clocks | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        children: [
          {
            path: ':timeClockId',
            lazy: () => loadingChunkRetry(() => import('@web/routes/time-clocks.$id')),
            children: [
              {
                path: 'breaks',
                lazy: () => loadingChunkRetry(() => import('@web/routes/time-clocks.$id')),
              },
              {
                path: 'location',
                lazy: () => loadingChunkRetry(() => import('@web/routes/time-clocks.$id')),
              },
            ],
          },
        ],
      },
      {
        path: 'time-preferences',
        lazy: () => loadingChunkRetry(() => import('./routes/time-preferences')),
        handle: {
          meta: () => ({
            title: msg`Time preferences | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        children: [
          {
            index: true,
            lazy: () => loadingChunkRetry(() => import('./routes/time-preferences._index')),
          },
          {
            path: 'time-gathering',
            lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.time-gathering')),
          },
          {
            path: 'notifications',
            lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.notifications')),
          },
          {
            path: 'advanced',
            lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.advanced')),
          },
          {
            path: 'download-app',
            lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.download-app')),
          },
          {
            path: 'demo-settings',
            lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.demo-settings')),
          },
          {
            path: 'overtime',
            lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.overtime')),
            children: [
              {
                index: true,
                lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.overtime._index')),
              },
              {
                path: 'rulesets',
                lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.overtime.rulesets')),
              },
              {
                path: 'employees',
                lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.overtime.employees')),
              },
              {
                path: '*',
                Component: NotFoundMessage,
              },
            ],
          },
          {
            path: 'leave-approvals',
            lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.leave-approvals')),
          },
          {
            path: 'leave-policies',
            lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.leave-policies')),
          },
          {
            path: 'time-tracking',
            lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.time-tracking')),
          },
          {
            path: 'work-locations',
            lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.work-locations')),
          },
          {
            path: '*',
            Component: NotFoundMessage,
          },
        ],
      },
      {
        path: '*',
        handle: {
          meta: () => ({
            title: msg`Page not found | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        Component: NotFoundPage,
      },
    ],
  },
]);
